import { webPublicRuntimeEnv } from "./runtime.generated";

const API_URL_DEFAULT = "http://localhost:8080/api/v1";
const GA_MEASUREMENT_ID_DEFAULT = "G-3BBENBX8DP";

type GeneratedPublicEnv = typeof webPublicRuntimeEnv;

export type WebPublicEnv = Omit<
  GeneratedPublicEnv,
  | "NEXT_PUBLIC_API_URL"
  | "NEXT_PUBLIC_SUPABASE_URL"
  | "NEXT_PUBLIC_SUPABASE_ANON_KEY"
  | "NEXT_PUBLIC_GA_MEASUREMENT_ID"
> & {
  NEXT_PUBLIC_API_URL: string;
  NEXT_PUBLIC_SUPABASE_URL: string;
  NEXT_PUBLIC_SUPABASE_ANON_KEY: string;
  NEXT_PUBLIC_GA_MEASUREMENT_ID: string;
};

function required(value: string | undefined, name: string, skipValidation: boolean): string {
  if (value?.length) return value;
  if (skipValidation) return "";
  throw new Error(`Missing required public environment variable: ${name}`);
}

function url(value: string, name: string, skipValidation: boolean): string {
  if (skipValidation || value.length === 0) return value;
  try {
    new URL(value);
    return value;
  } catch {
    throw new Error(`Invalid URL in public environment variable: ${name}`);
  }
}

/**
 * Resolve the generated public runtime contract without shipping a schema
 * validator to every browser route. Production values remain owned by
 * runtime_contracts.yaml; this boundary only enforces required/URL semantics.
 *
 * Server-only values deliberately live in runtime.generated.ts
 * and never enter this client-safe object.
 * @internal
 */
export function resolveWebPublicEnv(
  source: GeneratedPublicEnv,
  skipValidation = process.env.SKIP_ENV_VALIDATION === "true"
): WebPublicEnv {
  const apiUrl = source.NEXT_PUBLIC_API_URL ?? API_URL_DEFAULT;
  const supabaseUrl = required(
    source.NEXT_PUBLIC_SUPABASE_URL,
    "NEXT_PUBLIC_SUPABASE_URL",
    skipValidation
  );
  const sentryDsn = source.NEXT_PUBLIC_SENTRY_DSN;

  return {
    ...source,
    NEXT_PUBLIC_API_URL: url(apiUrl, "NEXT_PUBLIC_API_URL", skipValidation),
    NEXT_PUBLIC_SUPABASE_URL: url(supabaseUrl, "NEXT_PUBLIC_SUPABASE_URL", skipValidation),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: required(
      source.NEXT_PUBLIC_SUPABASE_ANON_KEY,
      "NEXT_PUBLIC_SUPABASE_ANON_KEY",
      skipValidation
    ),
    NEXT_PUBLIC_GA_MEASUREMENT_ID:
      source.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? GA_MEASUREMENT_ID_DEFAULT,
    NEXT_PUBLIC_SENTRY_DSN: sentryDsn
      ? url(sentryDsn, "NEXT_PUBLIC_SENTRY_DSN", skipValidation)
      : undefined,
  };
}

export const env = resolveWebPublicEnv(webPublicRuntimeEnv);
